import { UserPreferences } from './schema-copy/api-schema';
import { isNativeApp, nativePlatform } from './utils/platform';

// matchMedia is checked defensively so 'auto' degrades to 'light' on platforms
// without prefers-color-scheme support.
const darkQuery = window.matchMedia?.('(prefers-color-scheme: dark)');

export const supportsAutoTheme = !!darkQuery;

// The theme of users who never chose one: the apps follow the OS like other native apps do, the
// website stays light.
export const defaultTheme: NonNullable<UserPreferences['theme']> = isNativeApp && supportsAutoTheme ? 'auto' : 'light';

const LIGHT_META_COLOR = '#4C86F3';   // must match the default in index.html
const DARK_META_COLOR = '#181e28';    // --dr-bg-sidebar (dark)
// The apps' status bar on Android: the page surface (--surface in downread-theme.css).
const LIGHT_SURFACE = '#ffffff', DARK_SURFACE = '#161b24';

let currentTheme: UserPreferences['theme'];

/** Apply the theme preference to the document. Call on startup and whenever the
 *  preference changes; 'auto' is kept in sync with the OS via a media query listener. */
export function applyTheme(theme: UserPreferences['theme']): void {
    currentTheme = theme ?? defaultTheme;
    const dark = currentTheme == 'dark' || (currentTheme == 'auto' && !!darkQuery?.matches);
    document.documentElement.classList.toggle('dark', dark);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? DARK_META_COLOR : LIGHT_META_COLOR);
    applyNativeTheme();
}

// The apps' system bars follow the app's theme, also when it differs from the system setting.
// Android: Cordova's built-in status bar color, whose icons adapt to it. iOS: the window's light/dark
// appearance (cordova/local-plugins/system-theme), which the status bar text follows.
function applyNativeTheme(): void {
    if (!nativePlatform || !currentTheme) return;
    const w = window as any;
    if (nativePlatform == 'android')
        w.statusbar?.setBackgroundColor(document.documentElement.classList.contains('dark') ? DARK_SURFACE : LIGHT_SURFACE);
    else
        w.cordova?.plugins?.systemTheme?.setStyle(currentTheme);
}

// The first applyTheme() runs before Cordova is ready.
if (nativePlatform)
    document.addEventListener('deviceready', applyNativeTheme);

darkQuery?.addEventListener('change', () => {
    if (currentTheme == 'auto')
        applyTheme(currentTheme);
});
